/** Badge colour: `neutral` plus the accents (platform tones are reserved for their platform), `inverted` solid ink, `accent` solid accent blue for a count that must read over any picture, `quiet` bare text, `outline` a dashed hairline for a slot nothing fills yet, `ring` a solid hairline with no fill. */
export type BadgeTone =
  | 'neutral'
  | 'outline'
  | 'ring'
  | 'inverted'
  | 'accent'
  | 'quiet'
  | 'blue'
  | 'green'
  | 'orange'
  | 'red'
  | 'violet'
  | 'linkedin'
  | 'instagram'
  | 'x'
  | 'telegram'

/** The tone to background and text classes: a tint and a text colour, never a ring or an edge. */
export const BADGE_TONE_CLASSES: Record<BadgeTone, string> = {
  neutral: 'bg-bg-tertiary text-text-secondary',
  outline: 'outline-1 outline-dashed -outline-offset-1 outline-border-strong text-text-tertiary',
  ring: 'inset-ring-1 inset-ring-border text-text-secondary',
  inverted: 'bg-bg-inverted text-bg-primary',
  accent: 'bg-accent-blue text-text-on-accent',
  quiet: 'text-text-tertiary',
  blue: 'bg-accent-blue/10 text-accent-blue',
  green: 'bg-accent-green/10 text-accent-green',
  orange: 'bg-accent-orange/10 text-accent-orange',
  red: 'bg-accent-red/10 text-accent-red',
  violet: 'bg-accent-violet/10 text-accent-violet',
  linkedin: 'bg-platform-linkedin/10 text-platform-linkedin',
  instagram: 'bg-platform-instagram/10 text-platform-instagram',
  x: 'bg-platform-x/10 text-platform-x',
  telegram: 'bg-platform-telegram/10 text-platform-telegram',
}

/** The leading marker of a badge: a 6px `dot`, or a `pulse` dot that breathes while work is in flight. */
export type BadgeIndicator = 'dot' | 'pulse'

/** Badge size: `sm` labels a thing · `md` is a value the founder owns, such as a customer phrase or a region. */
export type BadgeSize = 'sm' | 'md'

export const BADGE_NEUTRAL_VALUE_CLASSES = 'bg-bg-tertiary text-text-primary'

/** The tones whose meta falls back to tertiary text; a tinted tone keeps its own hue. */
export const BADGE_PLAIN_TONES: ReadonlySet<BadgeTone> = new Set([
  'neutral',
  'outline',
  'ring',
  'inverted',
  'accent',
  'quiet',
])
